import React, { useState, useEffect } from "react";
import { Modal, OverlayTrigger, Tooltip, Pagination } from "react-bootstrap";
import "../../asset/css/styles.css";
import AddNewTask from "../../components/addtaskmodal/page";
import { useDrop, useDrag, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  getAllStory, acceptRTS,sendForRTS, getAllTeams,
  getAllSprints, getAllUsers, updateStoryPoints, updateStoryStatus,
} from "../../pages/api/adminDashboard";
import { stopSprint } from "../../pages/api/sprint";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MovePendingTask from "../../../src/components/movependingtask/page";
import { marked } from "marked";
import TaskDetails from "./taskdetails/page";

/* ── Badge helpers ─────────────────────────────────────────────────────────── */
const PRIORITY_STYLES = {
  HIGH:   { background: "#fdecea", color: "#e53935" },
  MEDIUM: { background: "#fff3e0", color: "#fb8c00" },
  LOW:    { background: "#e8f5e9", color: "#43a047" },
};

const TYPE_STYLES = {
  feature:     { background: "#e3f0ff", color: "#1565c0" },
  bug:         { background: "#fdecea", color: "#c62828" },
  task:        { background: "#e8f5e9", color: "#2e7d32" },
  enhancement: { background: "#fff3e0", color: "#e65100" },
  report:      { background: "#f3e5f5", color: "#6a1b9a" },
};

const getTypeBadgeStyle = (type) =>
  TYPE_STYLES[type?.toLowerCase()] || { background: "#e0e0e0", color: "#555" };

/* ── Small reusable components ─────────────────────────────────────────────── */
const PriorityBadge = ({ priority }) => {
  if (!priority) return null;
  const style = PRIORITY_STYLES[priority] || { background: "#e0e0e0", color: "#aaa" };
  return (
    <span className="badge-base" style={style}>
      {priority.charAt(0) + priority.slice(1).toLowerCase()}
    </span>
  );
};

const TypeBadge = ({ storyType }) => {
  if (!storyType) return null;
  return (
    <span className="badge-base" style={getTypeBadgeStyle(storyType)}>
      {storyType.charAt(0) + storyType.slice(1).toLowerCase()}
    </span>
  );
};

const AttachmentCount = ({ story }) => {
  const count = story.attachments?.length || story.attachmentCount || 0;
  if (!count) return null;
  return (
    <span className="as-attachment">
      <i className="bi bi-paperclip txt-11" />
      {count}
    </span>
  );
};

/* ── Progress bar ──────────────────────────────────────────────────────────── */
const SprintProgressBar = ({ progress = 0 }) => (
  <div className="as-progress-wrap">
    <div className="flex-between txt-12 mb-1">
      <span className="as-progress-label">Sprint progress</span>
      <span className="as-progress-pct">{progress}%</span>
    </div>
    <div className="as-progress-track">
      {/* width must stay inline — it's a dynamic JS value */}
      <div className="as-progress-bar" style={{ width: `${progress}%` }} />
    </div>
  </div>
);

/* ── Drag / Drop primitives ────────────────────────────────────────────────── */
const DropCell = ({ status, storyId, onDrop, className, children }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ["SUBTASK", "STORY"],
    drop: (item) =>
      item.type === "STORY"
        ? onDrop(null, status, item.storyId, true)
        : onDrop(item.id, status, storyId),
    collect: (monitor) => ({ isOver: !!monitor.isOver() }),
  }));

  /* hover tint per column — must stay inline (dynamic) */
   const hoverBg = isOver
    ? status === "TODO"       ? "#e1e5eb"
    : status === "INPROGRESS" ? "#d9e1f9"
    : status === "INTESTING"  ? "#f3e8ff"
    : "#d4f7de"
    : undefined;

  return (
    <div
      ref={drop}
      className={`${className} scroll-thin`}
      style={{ minHeight: "100px", transition: "background 0.2s", backgroundColor: hoverBg }}
    >
      {children}
    </div>
  );
};

const DraggableStoryCard = ({ story, children }) => {
  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: "STORY",
      item: { type: "STORY", storyId: story.id },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [story.id],
  );

  /* opacity stays inline — driven by isDragging state */
  return (
    <div
      ref={dragRef}
      style={{
        background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px",
        padding: "8px 10px", marginBottom: "8px", cursor: "grab",
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      {children}
    </div>
  );
};

/* ── Shared story card layout (used by all 3 columns) ──────────────────────── */
const StoryCard = ({ story, onView, editablePoints, onEditPoints, onTyping, onSave }) => (
  <>
    {/* Top row */}
    <div className="as-card-top-row">
      <span className="as-card-id">#{story.id}</span>
      <div className="as-card-badges">
        <TypeBadge storyType={story.storyType} />
      </div>
    </div>

    {/* Title */}
    <div className="as-card-title">{story.storyTitle}</div>

    {/* Description */}
    {story.storyDescription && (
      <div className="as-card-desc">
        {story.storyDescription.replace(/[#*_`>\[\]]/g, "").trim()}
      </div>
    )}

    {/* Bottom row */}
    <div className="as-card-bottom-row">
      <div className="as-card-bottom-left">
        <PriorityBadge priority={story.priority} />

        {/* SP — editable variant for TODO, read-only for others */}
        <span className="issue-card-sp">
          {editablePoints ? (
            story.editPoints ? (
              <input
                type="number" step="0.5" value={story.tempPoints}
                onChange={(e) => onTyping(story.id, e.target.value)}
                onBlur={() => onSave(story.id)}
                onKeyDown={(e) => e.key === "Enter" && onSave(story.id)}
                autoFocus
                className="as-sp-input"
              />
            ) : (
              <>
  <span className="as-sp-click" onClick={() => onEditPoints(story.id, story.storyPoints)}>
{`${story.storyPoints != null ? parseFloat(story.storyPoints).toFixed(1).replace(/\.0$/, "") : "-"} SP`}  </span>
  {story.testStoryPoints != null && Number(story.testStoryPoints) > 0 && (
    <span className="as-sp-click" style={{ marginLeft: 4, cursor: "default" }}>
{`/ ${parseFloat(story.testStoryPoints).toFixed(1).replace(/\.0$/, "")} SP`}    </span>
  )}
</>
            )
          ) : (
           <>
  <span>{story.storyPoints ? `${story.storyPoints} SP` : "- SP"}</span>
{story.testStoryPoints != null && Number(story.testStoryPoints) > 0 && (
  <span style={{ marginLeft: 4 }}>{`/ ${story.testStoryPoints} SP`}</span>
)}
</>
          )}
        </span>

      </div>

      <div className="as-card-bottom-right">
        {/* Avatars — only shown in TODO column where onView passes storyUsers */}
        {story.storyUsers && (
          <div className="issue-card-avatars">
            {story.storyUsers.filter((su) => su.user?.name).map((su) => (
              <OverlayTrigger key={su.id} placement="top" overlay={<Tooltip>{su.user.name}</Tooltip>}>
                <div className="issue-avatar">{getInitials(su.user.name)}</div>
              </OverlayTrigger>
            ))}
          </div>
        )}
        
        <OverlayTrigger placement="top" overlay={<Tooltip>View task</Tooltip>}>
          <button className="issue-card-icon-btn" onClick={() => onView(story)}>
            <i className="bi bi-eye" />
          </button>
        </OverlayTrigger>
      </div>
    </div>

    {/* Bottom meta row — date, comments, attachments */}
    <div className="as-card-meta-row">
      {story.sprint?.startDate && (
        <span className="as-card-date">
          <i className="bi bi-calendar3 txt-11" />
          {formatDate(story.sprint.startDate)}
        </span>
      )}
      <div className="as-card-meta-right">
  {((story.commentCount ?? story.comments?.length ?? 0) > 0) && (
    <span className="as-card-meta-item">
      <i className="bi bi-chat txt-11" />
      {story.commentCount ?? story.comments?.length ?? 0}
    </span>
  )}
  {(story.attachments?.length > 0) && (
    <span className="as-card-meta-item">
      <i className="bi bi-paperclip txt-11" />
      {story.attachments.length}
    </span>
  )}
</div>
    </div>

    {/* RTS bar */}
    {story.sentForRTS && !story.rtsAccepted && <div className="rts-bar sent">Sent for RTS</div>}
    {story.rtsAccepted && <div className="rts-bar accepted">✓ Accepted</div>}
  </>
);

/* ── Helpers (module-level, no React needed) ───────────────────────────────── */
const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(" ");
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });

/* ── Sunday-free day counting ── */
const dayNum = (s) => {
  const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
  return Date.UTC(y, m - 1, d) / 86400000;
};
const isSunday = (n) => (((n % 7) + 7) % 7) === 3;          // 3 = Sunday
const countNonSundays = (from, count) => {
  let c = 0;
  for (let i = 0; i < count; i++) if (!isSunday(from + i)) c++;
  return c;
};

// positive = days left, negative = overdue (Sundays not counted)
const sprintDaysLeft = (endDate) => {
  if (!endDate) return null;
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / 86400000;
  const end = dayNum(endDate);
  const diff = end - today;
  if (diff >= 0) return countNonSundays(today, diff);
  const n = countNonSundays(end + 1, -diff);
  return n ? -n : 0;
};

/* ── Pagination ────────────────────────────────────────────────────────────── */
const CustomPagination = ({ currentPage, totalPages, onPageChange }) => {
  const maxVisible = 3;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end   = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  return (
    <Pagination className="justify-content-center mt-3">
      <Pagination.First onClick={() => onPageChange(1)}           disabled={currentPage === 1} />
      <Pagination.Prev  onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} />

      {start > 1 && (
        <> <Pagination.Item onClick={() => onPageChange(1)}>1</Pagination.Item>
           {start > 2 && <Pagination.Ellipsis />} </>
      )}

      {pages.map((p) => (
        <Pagination.Item key={p} active={p === currentPage} onClick={() => onPageChange(p)}>
          {p}
        </Pagination.Item>
      ))}

      {end < totalPages && (
        <> {end < totalPages - 1 && <Pagination.Ellipsis />}
           <Pagination.Item onClick={() => onPageChange(totalPages)}>{totalPages}</Pagination.Item> </>
      )}

      <Pagination.Next onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} />
      <Pagination.Last onClick={() => onPageChange(totalPages)}      disabled={currentPage === totalPages} />
    </Pagination>
  );
};

/* ════════════════════════════════════════════════════════════════════════════ */
const ActiveSprint = () => {

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [showAddTaskModal, setShowAddTaskModal]     = useState(false);
  const [showViewTaskModal, setShowViewTaskModal]   = useState(false);
  const [stories, setStories]                       = useState([]);
  const [currentStory, setCurrentStory]             = useState(null);
  const [loading]                                   = useState(false);
  const token                                       = localStorage.getItem("token");
  const [, setTeams]        = useState([]);
  const [, setSprints]      = useState([]);
  const [, setUsers]        = useState([]);
  const [, setProductOwners]= useState([]);
  const [currentPage, setCurrentPage]               = useState(1);
  const [itemsPerPage, setItemsPerPage]             = useState(20);
  const [searchTerm, setSearchTerm]                 = useState("");
  const [searchType]                                = useState("both");
  const [sprintProgress, setSprintProgress]         = useState(0);
  const [, setPreviewHtml]  = useState("");
  const [showMovePendingTask, setShowMovePendingTask] = useState(false);
  const [selectedExpiredSprint, setSelectedExpiredSprint] = useState(null);
const [isAccpetLoading, setIsAccpetLoading]       = useState(false);
  const [isSendingRTS, setIsSendingRTS]             = useState(false);

  const roleId = Number(localStorage.getItem("roleId"));
  let isAdmin = false;
  let isTeamLead = false;
  if (roleId === 1 || roleId === 2) {
    isAdmin = true;
  } else if (roleId === 6) {
    isTeamLead = true;
  }
  
    const [isStopping, setIsStopping] = useState(false);
  const [showStopSprintModal, setShowStopSprintModal] = useState(false);
const [allSprints, setAllSprints]                   = useState([]);
const [moveAction, setMoveAction]                   = useState("backlog");
const [targetSprintId, setTargetSprintId]           = useState("");

  const [selectedTeam, setSelectedTeam] = useState(() => {
    const t = localStorage.getItem("selectedTeam");
    return (!t || t === "ALL") ? null : JSON.parse(t);
  });

  const [selectedSprint, setSelectedSprint] = useState(() => {
    const s = localStorage.getItem("selectedSprint");
    return s ? JSON.parse(s) : null;
  });

  /* ── Effects ─────────────────────────────────────────────────────────────── */
  useEffect(() => {
    const t = localStorage.getItem("selectedTeam");
    if (!t) setSelectedTeam(null);
  }, []);

  useEffect(() => {
    setPreviewHtml(marked(currentStory?.storyDescription || ""));
  }, [currentStory?.storyDescription]);

  useEffect(() => {
    const sync = () => {
      const t = localStorage.getItem("selectedTeam");
      setSelectedTeam(t ? JSON.parse(t) : null);
    };
    window.addEventListener("storage",     sync);
    window.addEventListener("teamChanged", sync);
    return () => {
      window.removeEventListener("storage",     sync);
      window.removeEventListener("teamChanged", sync);
    };
  }, []);

  useEffect(() => {
    const sync = () => {
      const s = localStorage.getItem("selectedSprint");
      setSelectedSprint(s ? JSON.parse(s) : null);
    };
    window.addEventListener("sprintChanged", sync);
    return () => window.removeEventListener("sprintChanged", sync);
  }, []);

  const refreshStories = async () => {
    const res = await getAllStory(token);
        console.log("Story #46 data:", res.data.find(s => s.id === 46));

    setStories(res.data.sort((a, b) => b.id - a.id));
  };
  useEffect(() => {
  if (selectedSprint) {
    refreshStories();
  }
}, [selectedSprint?.id]);

  useEffect(() => {
    window.addEventListener("storyUpdated",       refreshStories);
    window.addEventListener("storyListUpdated",   refreshStories);
    window.addEventListener("sprintListUpdated",  refreshStories);
    window.addEventListener("sprintStoryUpdated", refreshStories);
    return () => {
      window.removeEventListener("storyUpdated",       refreshStories);
      window.removeEventListener("storyListUpdated",   refreshStories);
      window.removeEventListener("sprintListUpdated",  refreshStories);
      window.removeEventListener("sprintStoryUpdated", refreshStories);
    };
  }, [token]);

  useEffect(() => {
  const handleCommentCount = (e) => {
    const { storyId, commentCount } = e.detail;
    setStories((prev) =>
      prev.map((s) => s.id === storyId ? { ...s, commentCount } : s)
    );
  };
  window.addEventListener("commentCountUpdated", handleCommentCount);
  return () => window.removeEventListener("commentCountUpdated", handleCommentCount);
}, []);

 const checkSprintEnd = () => {};

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const response = await getAllStory(token);
        const sorted   = response.data.sort((a, b) => b.id - a.id);
        setStories(sorted);
        checkSprintEnd(sorted);

        const today = new Date().toISOString().split("T")[0];
        const active = sorted.find(
  (s) => s.sprint && s.sprint.sprintStatus === "ACTIVE",
);
if (active?.sprint) {
  setSelectedSprint(active.sprint);
  localStorage.setItem("selectedSprint", JSON.stringify(active.sprint));
  window.dispatchEvent(new Event("sprintChanged"));
} else {
  // Find active sprint directly from sprints API even if no stories
  const sprintRes = await getAllSprints(token);
  console.log("sprint api",sprintRes.data);
  const activeSprint = sprintRes.data?.find(s => s.sprintStatus === "ACTIVE");
  if (activeSprint) {
    setSelectedSprint(activeSprint);
    localStorage.setItem("selectedSprint", JSON.stringify(activeSprint));
    window.dispatchEvent(new Event("sprintChanged"));
  }
}
      } catch (err) {
        console.error("Error fetching stories:", err);
        toast.error("Failed to load stories");
      }
    };
    fetchStories();
  }, [token]);

  useEffect(() => {
    const load = async () => {
      try {
        const [tr, sr, ur] = await Promise.all([
          getAllTeams(token), getAllSprints(token), getAllUsers(token),
        ]);
        setTeams(tr.data);
        setSprints(sr.data);
        setAllSprints(sr.data);
        setUsers(ur.data.filter((u) => u.role?.id === 3 && !u.productOwner && u.status === "Verified"));
        setProductOwners(ur.data.filter((u) => u.productOwner === true));
      } catch { console.error("Failed to load initial data"); }
    };
    load();
  }, [token]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, searchType, selectedTeam, selectedSprint]);

  /* ── Handlers ────────────────────────────────────────────────────────────── */
  const handleDrop = async (subtaskId, newStatus, storyId, isStoryDrop = false) => {
    const story = stories.find((s) => s.id === storyId);
    if (story?.sentForRTS || story?.rtsAccepted) {
      toast.error(story.rtsAccepted ? "RTS already accepted. Cannot move story." : "RTS sent. Cannot move story.");
      return;
    }
    if (!isStoryDrop) return;

    try {
      await updateStoryStatus(storyId, newStatus, token);
      setStories((prev) => prev.map((s) => s.id === storyId ? { ...s, status: newStatus } : s));
const label = newStatus === "INPROGRESS" ? "In Development"
  : newStatus === "INTESTING"  ? "In Testing"
  : newStatus === "COMPLETED"  ? "Completed"
  : newStatus;      toast.success(`Story moved to ${label} successfully!`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to update story status");
    }
  };

  const handleViewTaskShow = (story) => {
  const normalizedUsers = (story.storyUsers || []).map((su) => ({
    ...su,
    userRole: su.userRole && su.userRole !== "DEVELOPER"
      ? su.userRole
      : su.user?.isQaFlag
        ? "TESTER"
        : "DEVELOPER",
  }));
  setCurrentStory({
    ...story,
    attachments: story.attachments || [],
    storyUsers: normalizedUsers,
    commentCount: story.commentCount || 0,  // ✅ ADD THIS
  });
  setShowViewTaskModal(true);
};
  const handleViewTaskClose = () => { setShowViewTaskModal(false); setIsAccpetLoading(false); };
  const handleStopSprint = async () => {
    if (!selectedSprint || isStopping) return;
    setIsStopping(true);
    setShowStopSprintModal(false);
    try {
      await stopSprint(selectedSprint.id, token, moveAction, targetSprintId || null);
      toast.success(`${selectedSprint.sprintName} stopped successfully!`);
      setSelectedSprint(null);
      localStorage.removeItem("selectedSprint");
      const res = await getAllStory(token);
      const sorted = res.data.sort((a, b) => b.id - a.id);
      setStories(sorted);
      const newActive = sorted.find((s) => s.sprint?.sprintStatus === "ACTIVE");
      if (newActive?.sprint) {
        setSelectedSprint(newActive.sprint);
        localStorage.setItem("selectedSprint", JSON.stringify(newActive.sprint));
      }
      window.dispatchEvent(new Event("sprintListUpdated"));
      window.dispatchEvent(new Event("sprintChanged"));
    } catch (err) {
      const msg = err?.response?.data || "Failed to stop sprint.";
      toast.error(typeof msg === "string" ? msg : "Failed to stop sprint.");
    } finally {
      setIsStopping(false);
    }
  };
  const handleAddNewStory   = (s)  => { setStories((prev) => [s, ...prev]); setShowAddTaskModal(false); };

  const enableStoryPointEdit = (id, val) =>
    setStories((prev) => prev.map((s) =>
      s.id === id ? { ...s, editPoints: true, tempPoints: val != null ? val.toString() : "" } : s,
    ));

  const handleStoryPointTyping = (id, val) =>
    setStories((prev) => prev.map((s) => s.id === id ? { ...s, tempPoints: val } : s));

  const saveStoryPoints = async (id) => {
    const story = stories.find((s) => s.id === id);
    const pts   = parseFloat(story.tempPoints);
    if (isNaN(pts)) {
      toast.error("Please enter a valid number");
      setStories((prev) => prev.map((s) => s.id === id ? { ...s, editPoints: false } : s));
      return;
    }
    try {
      await updateStoryPoints(id, pts, token);
      setStories((prev) => prev.map((s) =>
        s.id === id ? { ...s, storyPoints: pts, editPoints: false, tempPoints: undefined } : s,
      ));
    } catch {
      setStories((prev) => prev.map((s) => s.id === id ? { ...s, editPoints: false } : s));
    }
  };

  const handleAcceptRTS = async (story) => {
    if (isAccpetLoading || !story) return;
    if (!story.sentForRTS)       { toast.warning("RTS can only be accepted after it has been sent."); return; }
    if (story.status !== "COMPLETED") { toast.warning("RTS can only be accepted when the story status is Done."); return; }

    try {
      setIsAccpetLoading(true);
      const res = await acceptRTS(story.id, token);
      if (res.status === 200) {
        toast.success("RTS accepted successfully!");
        setStories((prev) => prev.map((s) =>
          s.id === story.id ? { ...s, rtsAccepted: true, status: "COMPLETED" } : s,
        ));
        const map = JSON.parse(localStorage.getItem("storyStatusMap")  || "{}");
        map[story.id] = "COMPLETED";
        localStorage.setItem("storyStatusMap", JSON.stringify(map));
        const acc = JSON.parse(localStorage.getItem("rtsAcceptedMap") || "{}");
        acc[story.id] = true;
        localStorage.setItem("rtsAcceptedMap", JSON.stringify(acc));
        handleViewTaskClose();
      } else {
        toast.error("Failed to accept RTS.");
      }
    } catch {
      toast.error("Error while accepting RTS.");
      setIsAccpetLoading(false);
    }
  };

  const handleSendRTS = async (story) => {
    if (isSendingRTS || !story) return;
    if (story.status !== "COMPLETED") { toast.warning("Story must be Completed before sending for RTS."); return; }
    if (!story.prLink?.trim() || story.prLink === "null") { toast.warning("Please add a PR Link before sending for RTS."); return; }

    try {
      setIsSendingRTS(true);
      const res = await sendForRTS(story.id, token);
      if (res.status === 200) {
        toast.success("Sent for RTS successfully!");
        setStories((prev) => prev.map((s) =>
          s.id === story.id ? { ...s, sentForRTS: true } : s,
        ));
      } else {
        toast.error("Failed to send for RTS.");
      }
    } catch (e) {
      if (e?.response?.status === 429) {
        toast.warning("Email limit reached for sending RTS. Your request was still submitted.");
        setStories((prev) => prev.map((s) =>
          s.id === story.id ? { ...s, sentForRTS: true } : s,
        ));
      } else {
        toast.error("Error while sending for RTS.");
      }
    } finally {
      setIsSendingRTS(false);
    }
  };

  

  /* ── Derived data ────────────────────────────────────────────────────────── */
  const today = new Date().toISOString().split("T")[0];

 const filteredStories = stories
    .filter((story) => {
      if (!story.sprint) return false;
      if (story.sprint.startDate === null && story.sprint.endDate === null) return false;
      if (story.sprint.sprintName?.toLowerCase() === "backlog") return false;
      if (story.sprint.sprintStatus === "COMPLETED") return false;
      // ✅ A task's sprint must actually be started (ACTIVE) before it can
      // show on the Active Sprint board — this must be checked unconditionally,
      // even when a specific sprint is selected via the sprint switcher.
      // Previously, selecting a PLANNED sprint bypassed this check entirely,
      // so tasks moved from Backlog into a not-yet-started sprint still showed here.
      if (story.sprint.sprintStatus !== "ACTIVE") return false;
      if (selectedSprint) return Number(story.sprint.id) === Number(selectedSprint.id);
      const teamMatch = !selectedTeam || (
        Array.isArray(story.teams)
          ? story.teams.some((t) => Number(t.id) === Number(selectedTeam.id))
          : Number(story.team?.id) === Number(selectedTeam.id)
      );
      return teamMatch;
    })
   .filter((story) => {
      if (!searchTerm.trim()) return true;
      const t = searchTerm.toLowerCase().trim();
      const idMatch = String(story.id).toLowerCase().includes(t.replace(/^#/, ""));
      const titleMatch = story.storyTitle?.toLowerCase().includes(t);
      return idMatch || titleMatch;
    });
    
  const totalPages        = Math.ceil(filteredStories.length / itemsPerPage);
  const indexOfLastStory  = currentPage * itemsPerPage;
  const indexOfFirstStory = indexOfLastStory - itemsPerPage;
  const currentStories    = filteredStories.slice(indexOfFirstStory, indexOfLastStory);
// Sprint progress calculation (story-point based, same as Sprint Dashboard:
// Dev SP + Test SP per story)
useEffect(() => {
  if (!selectedSprint) { setSprintProgress(0); return; }

  // Use all stories of the sprint (not the search-filtered list), so typing
  // in the search box doesn't change the progress.
  const sprintStories = stories.filter(
    (s) => s.sprint && Number(s.sprint.id) === Number(selectedSprint.id)
  );

  const spOf = (s) =>
    (Number(s.storyPoints) || 0) + (Number(s.testStoryPoints) || 0);

  const totalSP = sprintStories.reduce((sum, s) => sum + spOf(s), 0);
  if (!totalSP) { setSprintProgress(0); return; }

  const completedSP = sprintStories
    .filter((s) => s.status === "COMPLETED")
    .reduce((sum, s) => sum + spOf(s), 0);

  setSprintProgress(Math.round((completedSP / totalSP) * 100));
}, [stories, selectedSprint?.id]);

  /* ── Sprint meta ─────────────────────────────────────────────────────────── */
  const daysLeft = sprintDaysLeft(selectedSprint?.endDate);   // Sundays not counted
  /* ════════════════════ RENDER ══════════════════════════════════════════════ */
  return (
    <DndProvider backend={HTML5Backend}>

<h5 className="as-heading page-main-heading">Active Sprint</h5>
      {/* ── Toolbar ── */}
      <div className="flex-between mb-1 as-toolbar">
  <div className="flex-center" style={{ gap: 8 }}>
    <span>Show:</span>
    <select
      className="form-select form-select-sm d-inline-block w-auto"
      value={itemsPerPage}
      onChange={(e) => { setItemsPerPage(parseInt(e.target.value)); setCurrentPage(1); }}
    >
{[10, 20].map((n) => <option key={n} value={n}>{n}</option>)}
    </select>
    <span>stories per page</span>

    {/* All filter — mobile only, right of stories per page */}
    {isMobile && (
      <select
        style={{
          border: "1px solid #d1d5db", borderRadius: "6px",
          padding: "4px 8px", fontSize: "12px", color: "#374151",
          background: "#fff", outline: "none", marginLeft: 4,
        }}
      >
        <option>All</option>
      </select>
    )}
  </div>

<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="input-group input-group-sm as-search-group">
            <span className="input-group-text as-search-icon-wrap">
              <i className="bi bi-search txt-11" />
            </span>
            <input
              type="text"
              className="form-control as-search-input"
              placeholder="Search Tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="btn btn-outline-secondary" onClick={() => setSearchTerm("")}>
                <i className="bi bi-x" />
              </button>
            )}
          </div>

          {selectedSprint && selectedSprint.sprintStatus === "ACTIVE" && (
            <button
              onClick={() => { setMoveAction("backlog"); setTargetSprintId(""); setShowStopSprintModal(true); }}
              disabled={isStopping}
              style={{
                padding: "5px 12px",
                fontSize: "13px",
                fontWeight: "500",
                background: isStopping ? "#fee2e2" : "#fff",
                color: "#dc2626",
                border: "1px solid #fca5a5",
                borderRadius: "6px",
                cursor: isStopping ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexShrink: 0,
                whiteSpace: "nowrap",
                opacity: isStopping ? 0.7 : 1,
              }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="#dc2626">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
              </svg>
              {isStopping ? "Stopping..." : "Stop Sprint"}
            </button>
          )}
        </div>
          
      </div>

      {/* ── Sprint info card ── */}
      {selectedSprint && selectedSprint.sprintStatus === "ACTIVE" && (
        <div className="as-sprint-card flex-between"style={{ gap: 24, marginBottom: 4 }}>
          <div className="as-sprint-left">
            <div className="flex-gap-10" style={{ marginBottom: 4 }}>
              <span className="as-sprint-name">{selectedSprint.sprintName}</span>
              {(daysLeft == null || daysLeft >= 0) && (
                <span className="as-sprint-active-badge">Active</span>
              )}
            </div>
            <div className="flex-gap-12 txt-12">
              <span className="as-sprint-dates">
                {formatDate(selectedSprint.startDate)} – {selectedSprint.endDate ? formatDate(selectedSprint.endDate) : "—"}            </span>
              {daysLeft != null && daysLeft >= 0 ? (
                <span className="flex-gap-4">
                  <i className="bi bi-clock txt-12 txt-grey" />
                  <span className="as-sprint-days-text">
                    {daysLeft} {daysLeft === 1 ? "day" : "days"} left
                  </span>
                </span>
              ) : daysLeft != null ? (
                <span className="as-sprint-ended">
                  Overdue by {Math.abs(daysLeft)} {Math.abs(daysLeft) === 1 ? "day" : "days"}
                </span>
              ) : null}
            </div>
          </div>
          <SprintProgressBar progress={sprintProgress} />
        </div>
      )}

      <div className="dashboard-container">
        <div className="board-wrap">

          {/* ── Column headers ── */}
          <div
            className="board-header"
            style={{
              position: "sticky",
              top: "-8px",
              zIndex: 5,
              background: "#fff",
              marginLeft: "-8px",
              marginRight: "-8px",
              marginTop: "-8px",
              paddingLeft: "8px",
              paddingRight: "8px",
              paddingTop: "8px",
            }}
          >
            {[
  { label: "To Do",          dot: "#9CA3AF", status: "TODO"      },
  { label: "In Development", dot: "#3B82F6", status: "INPROGRESS" },
  { label: "In Testing",     dot: "#A855F7", status: "INTESTING"  },
  { label: "Completed",      dot: "#22C55E", status: "COMPLETED"  },
].map(({ label, dot, status }) => (
  <div key={status} className="board-header-cell">
    <div className="board-header-left">
      <span className="board-dot" style={{ background: dot }} />
      <span className="board-col-title">{label}</span>
      <span className="board-col-count">
        {currentStories.filter((s) => s.status === status).length}
      </span>
    </div>
  </div>
))}
          </div>

          {/* ── Story rows ── */}
          {loading ? (
  <div className="as-board-empty">Loading...</div>
) : !selectedSprint || selectedSprint.sprintStatus !== "ACTIVE" ? (
  <div
    className="as-board-empty"
    style={{ fontSize: "22px", fontWeight: 700, color: "#374151", padding: "48px 0" }}
  >
    No active sprint available.
  </div>
) : currentStories.length === 0 ? (
  <div className="as-board-empty">
    {searchTerm ? "No matching stories or subtasks found" : "No stories available"}
  </div>
) : (
            currentStories.map((story) => {
  const effectiveStatus = (!story.status || !["TODO","INPROGRESS","INTESTING","COMPLETED"].includes(story.status))
    ? "TODO"
    : story.status;

  return (
    <div key={story.id} className="board-row">

      {/* TODO */}
      <DropCell status="TODO" onDrop={handleDrop} storyId={story.id} className="subtask-col-cell todo">
        {effectiveStatus === "TODO" && (
          <DraggableStoryCard story={story}>
            <StoryCard
              story={story}
              onView={handleViewTaskShow}
              editablePoints
              onEditPoints={enableStoryPointEdit}
              onTyping={handleStoryPointTyping}
              onSave={saveStoryPoints}
            />
          </DraggableStoryCard>
        )}
      </DropCell>

       {/* IN PROGRESS */}
      <DropCell status="INPROGRESS" onDrop={handleDrop} storyId={story.id} className="subtask-col-cell inprogress">
        {effectiveStatus === "INPROGRESS" && (
          <DraggableStoryCard story={story}>
            <StoryCard story={story} onView={handleViewTaskShow} />
          </DraggableStoryCard>
        )}
      </DropCell>

      {/* IN TESTING */}
      <DropCell status="INTESTING" onDrop={handleDrop} storyId={story.id} className="subtask-col-cell intesting">
        {effectiveStatus === "INTESTING" && (
          <DraggableStoryCard story={story}>
            <StoryCard story={story} onView={handleViewTaskShow} />
          </DraggableStoryCard>
        )}
      </DropCell>

      {/* DONE */}
      <DropCell status="COMPLETED" onDrop={handleDrop} storyId={story.id} className="subtask-col-cell done">
        {effectiveStatus === "COMPLETED" && (
          <DraggableStoryCard story={story}>
            <StoryCard story={story} onView={handleViewTaskShow} />
          </DraggableStoryCard>
        )}
      </DropCell>

    </div>
  );
})
          )}
        </div>

        {/* ── Pagination ── */}
        <div className="paginationBottom">
          {filteredStories.length > 0 && (
            <CustomPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>

        {/* ── Modals ── */}

        <TaskDetails
          showViewTaskModal={showViewTaskModal}
          setShowViewTaskModal={setShowViewTaskModal}
          currentStory={currentStory}
          setCurrentStory={setCurrentStory}
          onStoryUpdated={() => {}}
          showAcceptRTS={isAdmin}
          onAcceptRTS={handleAcceptRTS}
          isAcceptLoading={isAccpetLoading}
          showSendRTS={isTeamLead}
          onSendRTS={handleSendRTS}
          isSendingRTS={isSendingRTS}
        />

        <MovePendingTask
          show={showMovePendingTask}
          onClose={() => setShowMovePendingTask(false)}
          sprint={selectedExpiredSprint}
          onMoveSuccess={() => setShowMovePendingTask(false)}
        />
{showStopSprintModal && selectedSprint && (() => {
  const totalTasks       = filteredStories.length;
  const completedTasks   = filteredStories.filter((s) => s.status === "COMPLETED").length;
  const remainingTasks   = totalTasks - completedTasks;
  const spOf             = (s) => (parseFloat(s.storyPoints) || 0) + (parseFloat(s.testStoryPoints) || 0);
  const totalSP          = filteredStories.reduce((acc, s) => acc + spOf(s), 0);
  const completedSP      = filteredStories.filter((s) => s.status === "COMPLETED")
                             .reduce((acc, s) => acc + spOf(s), 0);
  const remainingSP      = +(totalSP - completedSP).toFixed(1);
  const completionPct    = totalSP > 0 ? Math.round((completedSP / totalSP) * 100) : 0;
  const remainingStories = filteredStories.filter((s) => s.status !== "COMPLETED");
  const availableSprints = allSprints.filter(
    (sp) => sp.id !== selectedSprint.id && sp.sprintStatus !== "COMPLETED"
  );
  const fmt = (d) => d
    ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "—";
  const statusMeta = {
    TODO:       { label: "To Do",          color: "#6B7280", bg: "#F3F4F6" },
    INPROGRESS: { label: "In Development", color: "#1D4ED8", bg: "#EFF6FF" },
    INTESTING:  { label: "In Testing",     color: "#7C3AED", bg: "#F5F3FF" },
    COMPLETED:  { label: "Completed",      color: "#15803D", bg: "#F0FDF4" },
  };
  return (
    <>
      <div className="ssm-backdrop" onClick={() => setShowStopSprintModal(false)} />
      <div className="ssm-modal scroll-thin">

        {/* Header */}
        <div className="ssm-header">
          <div className="ssm-header-left">
            <div className="ssm-warn-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                  stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <div className="ssm-title">Stop Sprint</div>
              <div className="ssm-subtitle">
                {selectedSprint.sprintName} · {fmt(selectedSprint.startDate)} – {fmt(selectedSprint.endDate)}
              </div>
            </div>
          </div>
          <button className="ssm-close-btn" onClick={() => setShowStopSprintModal(false)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="ssm-body">
          <div className="ssm-section-label">Sprint Summary</div>
          <div className="ssm-stats-grid">
            {[
              { label: "Total Tasks",     value: completedTasks,        of: totalTasks,          unit: "",     color: "#3B82F6", note: "completed of planned" },
              { label: "Story Points",    value: completedSP.toFixed(1), of: totalSP.toFixed(1), unit: " SP",  color: "#8B5CF6", note: "completed of planned" },
              { label: "Remaining Tasks", value: remainingTasks,        of: null,                unit: "",     color: remainingTasks > 0 ? "#EF4444" : "#22C55E", note: null },
            ].map(({ label, value, of, unit, color, note }) => (
              <div key={label} className="ssm-stat-card">
                <div className="ssm-stat-label">{label}</div>
                {of !== null ? (
                  <>
                    <div className="flex-gap-4">
                      <span className="ssm-stat-value" style={{ color }}>{value}{unit}</span>
                      <span className="ssm-stat-of">/ {of}{unit}</span>
                    </div>
                    <div className="ssm-stat-note">{note}</div>
                  </>
                ) : (
                  <div className="ssm-stat-value" style={{ color }}>{value}</div>
                )}
              </div>
            ))}
          </div>

          {/* Progress */}
          <div className="ssm-progress-wrap">
            <div className="ssm-progress-row">
              <span>Sprint completion</span>
              <span className={`ssm-progress-pct ${completionPct === 100 ? "complete" : "partial"}`}>
                {completionPct}%
              </span>
            </div>
            <div className="ssm-progress-track">
              <div
                className={`ssm-progress-bar ${completionPct === 100 ? "complete" : "partial"}`}
                style={{ width: `${completionPct}%` }}
              />
            </div>
          </div>

          {/* Status chips */}
          <div className="ssm-chips">
            {Object.entries(statusMeta).map(([status, meta]) => {
              const count = filteredStories.filter((s) => {
                const eff = (!s.status || !["TODO","INPROGRESS","INTESTING","COMPLETED"].includes(s.status))
                  ? "TODO" : s.status;
                return eff === status;
              }).length;
              return (
                <span key={status} className="ssm-chip"
                  style={{ background: meta.bg, border: `1px solid ${meta.color}22`, color: meta.color }}>
                  <span className="ssm-chip-dot" style={{ background: meta.color }} />
                  {meta.label}: {count}
                </span>
              );
            })}
          </div>
        </div>

        {/* Remaining tasks */}
        {remainingStories.length > 0 && (
          <div className="ssm-warn-box">
            <div className="ssm-warn-box-title">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="#D97706">
                <path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              </svg>
              {remainingStories.length} task{remainingStories.length > 1 ? "s" : ""} not completed
              {remainingSP > 0 && ` · ${remainingSP} SP remaining`}
            </div>
            <div className="ssm-remaining-list">
              {remainingStories.map((s) => {
                const eff = (!s.status || !["TODO","INPROGRESS","INTESTING","COMPLETED"].includes(s.status))
                  ? "TODO" : s.status;
                const meta = statusMeta[eff];
                return (
                  <div key={s.id} className="ssm-remaining-item">
                    <div className="ssm-remaining-left">
                      <span className="ssm-remaining-id">#{s.id}</span>
                      <span className="ssm-remaining-title">{s.storyTitle}</span>
                    </div>
                    <div className="ssm-remaining-right">
                      {s.storyPoints && <span className="ssm-remaining-sp">{s.storyPoints} SP</span>}
                      <span className="ssm-remaining-status"
                        style={{ background: meta.bg, color: meta.color }}>
                        {meta.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Move section */}
        {remainingStories.length > 0 && (
          <div className="ssm-move-section">
            <div className="ssm-section-label">Move Remaining Tasks To</div>
            <label className={`ssm-radio-option ${moveAction === "backlog" ? "selected" : ""}`}>
              <input type="radio" name="ssmMove" value="backlog"
                checked={moveAction === "backlog"}
                onChange={() => setMoveAction("backlog")} />
              <div>
                <div className="ssm-radio-title">Move to Backlog</div>
                <div className="ssm-radio-desc">Remaining tasks will be moved to Planning / Backlog.</div>
              </div>
            </label>
            <label className={`ssm-radio-option ${moveAction === "sprint" ? "selected" : ""}`}>
              <input type="radio" name="ssmMove" value="sprint"
                checked={moveAction === "sprint"}
                onChange={() => setMoveAction("sprint")} />
              <div style={{ flex: 1 }}>
                <div className="ssm-radio-title">Move to Another Sprint</div>
                <div className="ssm-radio-desc">Carry remaining tasks forward to a specific sprint.</div>
                {moveAction === "sprint" && (
                  <select className="ssm-sprint-select" value={targetSprintId}
                    onChange={(e) => setTargetSprintId(e.target.value)}
                    onClick={(e) => e.stopPropagation()}>
                    <option value="">— Select a sprint —</option>
                    {availableSprints.map((sp) => (
                      <option key={sp.id} value={sp.id}>{sp.sprintName}</option>
                    ))}
                  </select>
                )}
              </div>
            </label>
          </div>
        )}

        {/* Footer */}
        <div className="ssm-footer">
          <button className="ssm-btn-cancel" onClick={() => setShowStopSprintModal(false)} disabled={isStopping}>
            Cancel
          </button>
          <button className="ssm-btn-stop" onClick={handleStopSprint}
            disabled={isStopping || (moveAction === "sprint" && !targetSprintId)}>
            {isStopping && (
              <svg className="ssm-spin" width="14" height="14" viewBox="0 0 24 24"
                stroke="#fff" fill="none" strokeWidth="2.5">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
            )}
            <svg width="11" height="11" viewBox="0 0 24 24" fill="#fff">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
            </svg>
            {isStopping ? "Stopping..." : "Stop Sprint"}
          </button>
        </div>

      </div>
    </>
  );
})()}
        <ToastContainer />
      </div>
    </DndProvider>
  );
};

export default ActiveSprint;